import { useCallback, useEffect, useState } from 'react';

export type StepProps<T> = {
  close: () => void;
  data: T | undefined;
  setData: (data: T) => void;
  nextStep: (data?: T) => Promise<void>;
  canGoBack: boolean;
  previousStep: (data?: T) => void;
};

type StepComponent<T> = React.ComponentType<StepProps<T>>;

type Props<T> = {
  steps: StepComponent<T>[];
  initialData?: T;
  onComplete: (data: T) => void;
  onClose: () => void;
};

export default function ModalFlow<T>({ steps, initialData, onComplete, onClose }: Props<T>) {
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState(initialData);
  const Step = steps[currentStep];

  useEffect(() => {
    setData(initialData);
  }, [initialData]);

  const close = useCallback(() => {
    setData(undefined);
    setCurrentStep(0);
    onClose();
  }, [onClose]);

  const handleNextStep = async (stepData?: T) => {
    if (stepData !== undefined) {
      setData(stepData);
    }
    const nextStep = currentStep + 1;
    if (nextStep <= steps.length) {
      setCurrentStep(nextStep);
    }

    const payload = stepData ?? data;
    if (nextStep > steps.length - 1 && payload != null) {
      onComplete(payload);
    }
  };

  const handlePreviousStep = (stepData?: T) => {
    if (stepData !== undefined) {
      setData(stepData);
    }

    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (currentStep >= steps.length) {
    return null;
  }

  return (
    <Step
      close={close}
      data={data}
      setData={setData}
      nextStep={handleNextStep}
      previousStep={handlePreviousStep}
      canGoBack={currentStep > 0}
    />
  );
}
